import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import {
  AccountBalanceWallet as SafeIcon,
  Payments as CashIcon,
  HowToReg as AdminIcon,
  CardMembership as PassIcon,
  OpenInNew as DetailsIcon,
} from "@mui/icons-material";
import DataTable from "../DataTable";
import DetailModal from "./DetailModal";
import { formatCurrency } from "../../utils/formatters";

// "Sejf" view: how much cash should physically be in the safe for the selected
// date range. Cash = on-site single entries + admin-cleared entries + cash-paid
// passes. PayU and online-paid passes are excluded (they go to the bank).
const SafeReconciliation = ({ safeData }) => {
  const [detailModal, setDetailModal] = useState(null); // null | "cash" | "admin" | "passes"

  if (!safeData) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        Brak danych do wyświetlenia
      </Typography>
    );
  }

  const {
    locations = [],
    total_cash_entries = 0,
    total_admin_cleared = 0,
    total_cash_passes = 0,
    unattributed_cash_passes = 0,
    cash_entries_detail = [],
    admin_cleared_detail = [],
    cash_passes_detail = [],
  } = safeData;

  const safe_total = total_cash_entries + total_admin_cleared;

  const breakdown = [
    {
      key: "cash",
      label: "Wejścia gotówka",
      value: total_cash_entries,
      icon: <CashIcon />,
      detail: cash_entries_detail,
      type: "cash",
    },
    {
      key: "admin",
      label: "Oznaczone przez admina",
      value: total_admin_cleared,
      icon: <AdminIcon />,
      detail: admin_cleared_detail,
      type: "admin",
    },
    {
      key: "passes",
      label: "Karnety gotówką",
      value: total_cash_passes,
      icon: <PassIcon />,
      detail: cash_passes_detail,
      type: "passes",
    },
  ];

  const activeModal = breakdown.find((b) => b.key === detailModal);

  return (
    <Box>
      <Card
        sx={{
          mb: 3,
          color: "#fff",
          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
        }}
      >
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <SafeIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Powinno być w sejfie
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>
            {formatCurrency(safe_total)}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Gotówka + wejścia oznaczone przez admina w wybranym okresie
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {breakdown.map((b) => (
          <Grid item xs={12} sm={4} key={b.label}>
            <Paper
              sx={{
                p: 2,
                height: "100%",
                cursor: b.detail.length > 0 ? "pointer" : "default",
                transition: "box-shadow 0.15s",
                "&:hover": b.detail.length > 0 ? { boxShadow: 4 } : {},
              }}
              onClick={() => b.detail.length > 0 && setDetailModal(b.key)}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary" }}>
                  {b.icon}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {b.label}
                  </Typography>
                </Box>
                {b.detail.length > 0 && (
                  <DetailsIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatCurrency(b.value)}
              </Typography>
              {b.detail.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {b.detail.length} {b.type === "passes" ? "karnetów" : "wejść"}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {unattributed_cash_passes > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Karnety gotówką bez przypisania do lokalizacji (obejmują wiele lokalizacji):{" "}
          <strong>{formatCurrency(unattributed_cash_passes)}</strong>. Wliczone do sumy powyżej.
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Gotówka wg lokalizacji
        </Typography>
        {locations.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Brak gotówkowych płatności w tym okresie.
          </Typography>
        ) : (
          <DataTable
            size="small"
            rows={locations}
            getRowKey={(row) => row.location_id}
            columns={[
              { key: "location_name", label: "Lokalizacja" },
              {
                key: "cash_entries",
                label: "Wejścia gotówka",
                align: "right",
                render: (row) => formatCurrency(row.cash_entries),
              },
              {
                key: "admin_cleared_amount",
                label: "Admin",
                align: "right",
                render: (row) => formatCurrency(row.admin_cleared_amount),
              },
              {
                key: "cash_passes",
                label: "Karnety",
                align: "right",
                render: (row) => formatCurrency(row.cash_passes),
              },
              {
                key: "location_total",
                label: "Razem",
                align: "right",
                render: (row) => (
                  <Typography sx={{ fontWeight: 700 }}>{formatCurrency(row.location_total)}</Typography>
                ),
              },
            ]}
          />
        )}
      </Paper>

      {activeModal && (
        <DetailModal
          open={true}
          onClose={() => setDetailModal(null)}
          title={activeModal.label}
          rows={activeModal.detail}
          type={activeModal.type}
          formatCurrency={formatCurrency}
        />
      )}
    </Box>
  );
};

export default SafeReconciliation;
